/* eslint-disable @tencent/i18next/no-literal-string */
import { AnalyzerResultType } from '../../slide-content-analyzer.interface';
import {
    AnalyzerResultWidthContrast,
    ITextColorContrastResult,
    ITextElementBoxColorContrast,
} from '@src/slide/application/service/slide-rating-and-improve/color/color-analyzer.interface';
import {
    ColorBackType,
    IElementBack,
    TextColoContrastRule,
    UnknownColor,
} from '@src/slide/application/service/slide-rating-and-improve/base/color-base-analyzer.interface';
import {
    isStringVisible,
} from '@src/slide/application/service/slide-rating-and-improve/text/calculate/text-box-calculate';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IParagraph } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { setTextRunStyles } from '@src/slide/domain/ui-model/element-modeles/property/text/text-operation';
import Color from 'color';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { SolidFill } from '@src/slide/domain/ui-model/plugin/referModels/FillClass';
import i18n from '@i18next/i18n';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';

export class TextColorContrastResult implements ITextColorContrastResult {
    /**
     * 根据字体大小计算颜色之间的对比度阈值
     */
    public static getCompareRatioAndRule(fontSize: number): { ratio: number, rule: TextColoContrastRule } {
        let toCompareRatio = 2.8;
        let rule: TextColoContrastRule = TextColoContrastRule.normal;
        if (fontSize < 18) {
            toCompareRatio = 3.5;
            rule = TextColoContrastRule.small;
        } else if (fontSize < 50) {
            toCompareRatio = 2.8;
            rule = TextColoContrastRule.normal;
        } else if (fontSize < 100) {
            toCompareRatio = 2;
            rule = TextColoContrastRule.larger;
        } else {
            toCompareRatio = 1.5;
            rule = TextColoContrastRule.largest;
        }
        return {
            ratio: toCompareRatio,
            rule,
        };
    }

    /**
     * 计算颜色之间的对比度，WCAG 2.0规范的规则
     * @private
     * @param color1
     * @param color2
     */
    public static getColorsContrast(color1: Color, color2: Color) {
        const l1 = TextColorContrastResult.getColorRelativeLuminance(color1);
        const l2 = TextColorContrastResult.getColorRelativeLuminance(color2);
        return l1 > l2 ? (l1 + 0.05) / (l2 + 0.05) : (l2 + 0.05) / (l1 + 0.05);
    }

    /**
     * 颜色的相对亮度
     * @private
     */
    public static getColorRelativeLuminance(color: Color) {
        let r; let g; let b;
        r = color.red() / 255;
        g = color.green() / 255;
        b = color.blue() / 255;

        r = r <= 0.03928 ? r / 12.92 : ((r + 0.055) / 1.055) ** 2.4;
        g = g <= 0.03928 ? g / 12.92 : ((g + 0.055) / 1.055) ** 2.4;
        b = b <= 0.03928 ? b / 12.92 : ((b + 0.055) / 1.055) ** 2.4;

        return (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
    }

    public analyzerName: string;
    public description: string;
    public resultInfo: Array<ITextElementBoxColorContrast>;
    public resultType: AnalyzerResultType;

    private showResult: Array<AnalyzerResultWidthContrast> = [];
    public constructor(analyzerName: string, description: string) {
        this.analyzerName = analyzerName;
        this.description = description;
        this.reset();
    }

    public getShowResult(): AnalyzerResultWidthContrast[] {
        return this.showResult;
    }

    public reset() {
        this.resultType = AnalyzerResultType.SUCCESS;
        this.resultInfo = [];
        this.showResult = [];
    }

    public getCompareRatioOfRule(rule: TextColoContrastRule): number {
        switch (rule) {
            case TextColoContrastRule.normal:
                return 2.8;
            case TextColoContrastRule.small:
                return 3.5;
            case TextColoContrastRule.larger:
                return 2;
            case TextColoContrastRule.largest:
                return 1.5;
            default:
                return 2.8;
        }
    }

    // eslint-disable-next-line complexity
    public getDescriptionWithSelect(): Array<AnalyzerResultWidthContrast> | undefined {
        if (this.resultType === AnalyzerResultType.SUCCESS) return undefined;
        if (this.showResult.length > 0) return this.showResult;
        const result: Array<AnalyzerResultWidthContrast> = [];
        let lastTextBoxIndex = -1;
        let lastLineIndex = -1;
        let lastSlideIndex = -1;
        let lastText = '';
        let lastLineBox;
        let lastParaBox;
        let lastResult: ITextElementBoxColorContrast | undefined = undefined;
        let startIndex = 0;
        for (let i = 0; i < this.resultInfo.length; i++) {
            const current = this.resultInfo[i];
            const currentBox = current.boxNode.textBox;
            const currentSlideIndex = current.boxNode.slideNode.slideIndex;
            let currentTextIndex = -1;
            const currentLineBox = currentBox.parentBox;
            if (currentLineBox) {
                currentTextIndex = currentLineBox.children.indexOf(currentBox);
            }
            const currentParaBox = currentLineBox?.parentBox;
            let currentLineIndex = -1;
            if (currentLineBox && currentParaBox) {
                currentLineIndex = currentParaBox.children.indexOf(currentLineBox);
            }
            if (i === 0) {
                lastText = currentBox.text;
                lastResult = current;
                startIndex = 0;
            } else {
                // 同一行相邻或同一段内上一行最后一个和下一行第一个
                if (((currentLineBox === lastLineBox && lastTextBoxIndex + 1 === currentTextIndex)
                    || (lastParaBox === currentParaBox && lastLineIndex + 1 === currentLineIndex
                        && currentTextIndex === 0 && lastTextBoxIndex === lastLineBox.children.length - 1))
                    && lastResult
                    && lastResult.textBack.type === current.textBack.type
                    // @ts-ignore
                    && lastResult.textBack.color?.string() === current.textBack.color?.string()
                    && lastResult.textColor.string() === current.textColor.string()) {
                    // 同一行相邻
                    lastText += currentBox.text;
                    lastResult = current;
                } else {
                    if (lastText.length > 0 && isStringVisible(lastText) && lastResult) {
                        result.push({
                            description: this.getDescription(lastSlideIndex + 1, lastText),
                            selectable: true,
                            selectSlide: lastResult.boxNode.slideNode.slide,
                            selectElement: lastResult.boxNode.element,
                            handleProblem: this.handleItemClick,
                            contrastInfo: lastResult,
                            startIndex,
                            endIndex: i - 1,
                        });
                    }
                    startIndex = i;
                    lastText = currentBox.text;
                    lastResult = current;
                }
            }
            lastSlideIndex = currentSlideIndex;
            lastTextBoxIndex = currentTextIndex;
            lastLineBox = currentLineBox;
            lastParaBox = currentParaBox;
            lastLineIndex = currentLineIndex;
        }
        if (lastText.length > 0 && isStringVisible(lastText) && lastResult) {
            result.push({
                description: this.getDescription(lastSlideIndex + 1, lastText),
                selectSlide: lastResult.boxNode.slideNode.slide,
                selectElement: lastResult.boxNode.element,
                handleProblem: this.handleItemClick,
                contrastInfo: lastResult,
                startIndex,
                selectable: true,
                endIndex: this.resultInfo.length - 1,
            });
        }
        this.showResult = result;
        return result;
    }

    // eslint-disable-next-line complexity
    private handleItemClick = (index: number) => {
        if (this.showResult[index].selectSlide?.sdkData
            && this.showResult[index].selectElement?.sdkData) {
            const currentItem = this.showResult[index].contrastInfo;
            const { textColor } = currentItem;
            const textBackColor = currentItem.textBack.color;
            if (textBackColor === UnknownColor) return;
            const targetRatio = this.getCompareRatioOfRule(currentItem.contrastRule);
            const textHsl = textColor.hsl().array();
            const textL = textHsl[2];
            const backHsl = textBackColor.hsl().array();
            const backL = backHsl[2];
            const currentContrast = currentItem.contrast;
            let targetTextL;
            if (textL > backL) {
                targetTextL = (textL || 1) * targetRatio  / currentContrast;
                if (targetTextL / backL < targetRatio) {
                    targetTextL = backL * targetRatio;
                }
            } else {
                targetTextL = (textL || 1) * currentContrast / targetRatio;
                if (backL / targetTextL < targetRatio) {
                    targetTextL = backL / targetRatio;
                }
            }
            let targetTextColor = new Color([textHsl[0], textHsl[1], targetTextL], 'hsl').rgb();
            let newContrast = TextColorContrastResult.getColorsContrast(targetTextColor, textBackColor);
            if (backL < 10 && newContrast < targetRatio) {
                targetTextColor = new Color([255, 255, 255]);
                newContrast = TextColorContrastResult.getColorsContrast(targetTextColor, textBackColor);
            }
            if (targetTextL <= 100 && targetTextL >= 0 && newContrast >= targetRatio) {
                // 更新文字颜色
                const toUpdateParagraphList = this
                    .prepareTextColorParagraphList(
                        currentItem,
                        new TDColor({ rgbaStr: targetTextColor.string() }),
                        index,
                    );
                if (toUpdateParagraphList.length <= 0) return;
                const element: IShapeModel = currentItem.boxNode.element as IShapeModel;
                element.updateParagraphList(toUpdateParagraphList);
            } else {
                let targetBackL;
                if (textL > backL) {
                    targetBackL = (backL || 1) * currentContrast / targetRatio;
                    if (textL / targetBackL < targetRatio) {
                        targetBackL = textL / targetRatio;
                    }
                } else {
                    targetBackL = (backL || 1) * targetRatio / currentContrast;
                    if (targetBackL / textL  < targetRatio) {
                        targetBackL = textL * targetRatio;
                    }
                }
                if (targetBackL < 0 || targetBackL > 100) return;

                const targetBackColor = new Color([backHsl[0], backHsl[1], targetBackL], 'hsl').rgb();
                // 更新背景颜色
                this.updateTextBackColor(currentItem, targetBackColor, index);
            }

            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.AUTO_BEAUTIFY,
                action: TDWActionType.BEAUTIFY_CONTRAST,
            });
        }
        this.removeOneResult(index);
    };

    private removeOneResult(index: number) {
        this.showResult.splice(index, 1);
        if (this.showResult.length === 0) this.resultType = AnalyzerResultType.SUCCESS;
    }

    private updateTextBackColor(currentItem: ITextElementBoxColorContrast, backColor: Color, showIndex: number) {
        const tdColor = new TDColor({ rgbaStr: backColor.string() });
        if (currentItem.textBack.type === ColorBackType.SLIDE_BACKGROUND) {
            const { slide } = currentItem.boxNode.slideNode;
            slide.getBackgroundFill().updateCurrentFill({ solidFill: new SolidFill(tdColor) });
        } else if (currentItem.textBack.type === ColorBackType.ELEMENT) {
            const element: IShapeModel = (currentItem.textBack as IElementBack).elementNode.element as IShapeModel;
            element.getBackgroundFill().updateCurrentFill({ solidFill: new SolidFill(tdColor) });
        } else if (currentItem.textBack.type === ColorBackType.TEXT_BACK) {
            // 更新文字背景颜色
            const toUpdateParagraphList = this
                .prepareTextBackColorParagraphList(currentItem, backColor, showIndex);
            if (toUpdateParagraphList.length <= 0) return;
            const element: IShapeModel = currentItem.boxNode.element as IShapeModel;
            element.updateParagraphList(toUpdateParagraphList);
        }
    }

    private getDescription(slide: number, text: string): string {
        return i18n.t('第{0}页\'{1}\'和背景颜色对比度较低', { 0: slide, 1: text });
    }


    private prepareTextColorParagraphList(
        currentItem: ITextElementBoxColorContrast,
        tdColor: ITDColor,
        showIndex: number,
    ): IParagraph[] {
        const element: IShapeModel = currentItem.boxNode.element as IShapeModel;
        if (!element.paragraphList) return [];
        const { startIndex, endIndex } = this.showResult[showIndex];
        const paths: Array<Array<number>> = [];
        for (let i = startIndex; i <= endIndex; i++) {
            const currentResultInfo = this.resultInfo[i];
            // @ts-ignore
            const currentPath: Array<number> | undefined = currentResultInfo.boxNode.textBox.textStyle?.getCustomizeProp?.('slatePath');
            if (currentPath) {
                paths.push(currentPath);
            }
        }
        if (paths.length > 0) {
            const newPara = setTextRunStyles(element.paragraphList, { tdColor }, paths);
            return newPara;
        }
        return [];
    }

    private prepareTextBackColorParagraphList(
        currentItem: ITextElementBoxColorContrast,
        backColor: Color,
        showIndex: number,
    ): IParagraph[] {
        const element: IShapeModel = currentItem.boxNode.element as IShapeModel;
        if (!element.paragraphList) return [];
        const { startIndex, endIndex } = this.showResult[showIndex];
        const paths: Array<Array<number>> = [];
        for (let i = startIndex; i <= endIndex; i++) {
            const currentResultInfo = this.resultInfo[i];
            // @ts-ignore
            const currentPath: Array<number> | undefined = currentResultInfo.boxNode.textBox.textStyle?.getCustomizeProp?.('slatePath');
            if (currentPath) {
                paths.push(currentPath);
            }
        }
        if (paths.length > 0) {
            const newPara = setTextRunStyles(
                element.paragraphList,
                { backgroundColor: backColor.string() },
                paths,
            );
            return newPara;
        }
        return [];
    }
}
